import {
  Button,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormField,
  openDeleteDialog,
  openDialog,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, Heading, HStack, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useParams} from 'react-router-dom';

import {isNil, mergeAll} from 'ramda';
import {noop} from 'ramda-adjunct';

import {
  useDeleteCustomerContractAssignedCustomerMutation,
  useGetCustomerContractAssignedCustomerQuery,
  usePatchCustomerContractAssignedCustomerMutation,
  usePostCustomerContractAssignedCustomerMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {customerRoutes, testIds} from '@dms/routes';
import {CustomerMatchOrCreate, DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {composePath, Nullish, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {ValidityForm} from './components/ValidityForm';

type AssignedCustomerFormType = {
  defaultValidityDate: string | Nullish;
};

const PREDEFINED_COLUMNS = ['customerName'] as const;

export function AssignedCustomersTab() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetCustomerContractAssignedCustomerQuery({
    customerContractId: id ?? '',
  });

  const [patchAssignedCustomer] = usePatchCustomerContractAssignedCustomerMutation();
  const [postAssignedCustomer] = usePostCustomerContractAssignedCustomerMutation();
  const [deleteAssignedCustomer] = useDeleteCustomerContractAssignedCustomerMutation();

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {customerContractId: id ?? ''}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
  }) => {
    const customerId = getColumnData<{id: string}>('customerName')?.value?.id;

    if (isNil(customerId)) {
      showNotification.error();
      return;
    }

    match(actionKey)
      .with('redirectDetail', 'customerDetail', () =>
        navigate(composePath(customerRoutes.detail, {params: {id: customerId}}))
      )
      .with('editValidity', () =>
        openDialog(
          <ValidityForm
            customerContractId={id ?? ''}
            customerId={customerId}
            onClose={closeCurrentDialog}
            afterSubmit={() => refreshDmsDatagrid('customer-contract-assigned-customer')}
            data-testid={testIds.settings.customerContractsDetail('editValidity')}
          />,
          {
            title: i18n.t('entity.customerContract.labels.editValidity'),
            size: 'small',
          }
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteAssignedCustomer({
              customerContractId: id ?? '',
              customerId,
            })
              .unwrap()
              .then(() => refreshDmsDatagrid('customer-contract-assigned-customer'))
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  const handleAssign = () =>
    openDialog(
      <CustomerMatchOrCreate
        onCustomer={(customer) =>
          postAssignedCustomer({
            customerContractId: id ?? '',
            customerId: customer.id,
          })
            .unwrap()
            .then(() => {
              refreshDmsDatagrid('customer-contract-assigned-customer');
              closeCurrentDialog();
            })
            .catch(handleApiError)
        }
        data-testid={testIds.settings.customerContractsDetail('assignCustomerDialog')}
      />,
      {
        title: i18n.t('entity.customer.labels.customer'),
      }
    );

  const handleFormChange = (data: AssignedCustomerFormType) =>
    patchAssignedCustomer({
      customerContractId: id ?? '',
      body: {
        defaultValidityDate: data.defaultValidityDate ?? '',
      },
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <VStack spacing={4}>
      <Text color="secondary" size="small">
        {i18n.t('entity.customerContracts.labels.defaultValidityDateDescription')}
      </Text>
      <Box width={98}>
        <DataStatus isLoading={isLoading} isError={isError}>
          <Form<AssignedCustomerFormType> defaultValues={data} onChange={handleFormChange}>
            {(control) => (
              <FormField
                control={control}
                type="apiDate"
                name="defaultValidityDate"
                label={i18n.t('entity.customerContract.labels.defaultValidityDate')}
                data-testid={testIds.settings.customerContractsDetail('defaultValidityDate')}
              />
            )}
          </Form>
        </DataStatus>
      </Box>
      <Separator spacing={0} />
      <HStack justify="space-between" align="center">
        <Heading size={4}>{i18n.t('entity.customerContract.labels.assignedCustomers')}</Heading>
        <Button
          title={i18n.t('general.actions.assignCustomer')}
          variant="link"
          leftIcon="social/person_add"
          size="small"
          onClick={handleAssign}
          data-testid={testIds.settings.customerContractsDetail('assignCustomer')}
        />
      </HStack>
      <DmsDataGrid
        autoHeight
        gridCode="customer-contract-assigned-customer"
        queryModifier={queryModifier}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        data-testid={testIds.settings.customerContractsDetail('assignCustomersDG')}
      />
    </VStack>
  );
}
